import { useAccount } from "@apis/accounts/useAccounts"
import { useGetMarketCurrentPtcQuery } from "@apis/pricing/usePricing"
import type { TTitleValueItem } from "@components/TitleValueList/types"
import { formatDate, formatRate } from "@utils/formatters"

type TAccountUtilityHistoryData = {
  items: TTitleValueItem[]
  market_code: string | undefined
  revenue_class_code: string | undefined
}

export const useAccountUtilityHistorySerializer = (account_id: string) => {
  const getAccount = useAccount(account_id)
  const getCurrentPtc = useGetMarketCurrentPtcQuery(
    getAccount.data?.market_code,
    getAccount.data?.revenue_class_code,
  )
  const isError = getAccount.isError || getCurrentPtc.isError

  const data: TAccountUtilityHistoryData = {
    market_code: getAccount.data?.market_code,
    revenue_class_code: getAccount.data?.revenue_class_code,
    items: [
      {
        title: { content: "Utility:" },
        value: {
          content:
            getAccount.data?.market?.short_name ||
            getAccount.data?.market?.market_name ||
            getAccount.data?.market_code,
          isPending: getAccount.isPending,
        },
        className: "tw:md:col-start-1 tw:md:row-start-1",
      },
      {
        title: { content: "Market:" },
        value: {
          content: getAccount.data?.market?.market_code,
          isPending: getAccount.isPending,
        },
        className: "tw:md:col-start-1 tw:md:row-start-2",
      },
      {
        title: {
          content: getAccount.data?.market?.account_num_synonym
            ? `${getAccount.data.market.account_num_synonym}:`
            : "Account Number:",
          isPending: getAccount.isPending,
        },
        value: {
          content: getAccount.data?.account_number,
          isPending: getAccount.isPending,
          className: "tw:break-all",
        },
        className: "tw:md:col-start-2 tw:md:row-start-1",
      },
      {
        title: { content: "Tax Exempt:" },
        value: {
          content: getAccount.data?.is_tax_exempt ? "Yes" : "No",
          isPending: getAccount.isPending,
        },
        className: "tw:md:col-start-2 tw:md:row-start-2",
      },
      {
        title: { content: "Upcoming PTC:" },
        value: {
          content: (() => {
            const upcomingPtc = getCurrentPtc.data?.upcoming_price_to_compare
            const rate = formatRate(upcomingPtc?.rate, upcomingPtc?.rate_units)
            const fromDt = formatDate(upcomingPtc?.from_dt)

            return (
              rate &&
              fromDt && (
                <div className="tw:flex tw:flex-col">
                  <span>{`${rate} as of ${fromDt}`}</span>
                </div>
              )
            )
          })(),
          isPending: getCurrentPtc.isPending,
        },
        className: "tw:md:col-start-3 tw:md:row-start-1",
      },
      {
        title: { content: "Current PTC:" },
        value: {
          content: (() => {
            const foundRate = getCurrentPtc.data?.found_rate
            const rate = formatRate(
              getCurrentPtc.data?.rate,
              getCurrentPtc?.data?.rate_units,
            )
            const fromDt = formatDate(getCurrentPtc.data?.from_dt)

            return (
              foundRate && (
                <div className="tw:flex tw:flex-col">
                  {rate && fromDt && <span>{`${rate} as of ${fromDt}`}</span>}
                </div>
              )
            )
          })(),
          isPending: getCurrentPtc.isPending,
        },
        className: "tw:md:col-start-3 tw:md:row-start-2",
      },
      {
        title: { content: "Historical 2 year PTC Average:" },
        value: {
          content: (() => {
            const rate = formatRate(
              getCurrentPtc.data?.average_price_to_compare,
              getCurrentPtc.data?.rate_units,
            )

            return (
              rate && (
                <div className="tw:flex tw:flex-col">
                  <span>{rate}</span>
                </div>
              )
            )
          })(),
          isPending: getCurrentPtc.isPending,
        },
        className: "tw:md:col-start-3 tw:md:row-start-3",
      },
    ],
  }

  return {
    data,
    isError,
  }
}
